import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { LessonReader } from "@/pages/Topic";
import GemDivider from "@/components/GemDivider";
import { btnPrimary, btnSecondary, label } from "@/lib/styles";

// The one lesson readable without an account. No progress, no XP, no quiz:
// those need a user, and the quiz needs the LLM. This is the sample.
export default function FreeLesson() {
  usePageMeta(
    "Free Lesson: What Is an LLM? A Plain-English Mental Model",
    "Read the first Promptbound lesson free, no account needed: how large language models actually work, explained from zero with every symbol unpacked."
  );

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api
      .get("/public/lesson")
      .then(({ data }) => setData(data))
      .catch(() => setFailed(true));
  }, []);

  if (failed) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <p className="text-zinc-400">The archive is unreachable right now. Please try again shortly.</p>
        <Link to="/" className={`${btnSecondary} mt-6 inline-flex`}>Back to the gate</Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <span className="cursor-blink font-mono text-emerald-400">unsealing the scroll</span>
      </div>
    );
  }

  const { topic, chapters } = data;

  return (
    <div className="max-w-3xl mx-auto px-5 py-12">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500">
        <Sparkles size={12} className="text-emerald-400" />
        Free sample lesson, no account needed
      </div>

      <span className={`${label} mt-6 block`}>
        {topic.tier_meta?.title}: {topic.tier_meta?.subtitle}
      </span>
      <h1 className="mt-2 font-sans font-black text-3xl sm:text-4xl text-zinc-50">{topic.title}</h1>
      <p className="mt-4 text-zinc-400 leading-relaxed">{topic.scope_summary}</p>

      <LessonReader chapters={chapters} />

      <GemDivider />

      <div className="text-center">
        <h2 className="font-sans font-black text-2xl text-zinc-50">That was 1 of 40.</h2>
        <p className="mt-3 text-sm text-zinc-400 max-w-lg mx-auto">
          Every one of them is free, forever, no card. An account tracks your rank and XP through the
          other 39, and lets you face Altaris, the examiner who grades your answers against a rubric
          you never get to see.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <Link to="/register" className={btnPrimary} data-testid="free-lesson-register">
            Continue the journey <ArrowRight size={16} />
          </Link>
          <Link to="/curriculum" className={btnSecondary}>See all 40 lessons</Link>
        </div>
      </div>
    </div>
  );
}
